import { useState, useEffect } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { CatChat } from './chat/CatChat';

interface CatMascotProps {
  /** Custom message to display in speech bubble */
  message?: string;
  /** Variant of the cat - determines which image to show */
  variant?: 'wave' | 'study' | 'celebrate';
  /** Auto hide after X milliseconds (0 = don't auto hide) */
  autoHideMs?: number;
  /** Position on screen */
  position?: 'bottom-right' | 'bottom-left';
  /** Whether to show initially */
  showOnMount?: boolean;
}

const DEFAULT_MESSAGES = [
  'Chào bạn! Mình là Mèo Học Bá 🎓',
  'Meow~ Bạn muốn biết mình hợp nghề gì không?',
  'Làm bài CAT để khám phá bản thân nào! ✨',
  'Mình sẽ giúp bạn tìm ra con đường phù hợp nhất!',
  'Tương lai đang chờ bạn khám phá đó~ 🌟',
  'Click vào mình để chat nhé!',
  'Bạn có 6 năng lực tiềm ẩn, muốn biết không?',
  'Mèo tin bạn sẽ chọn được ngành học tuyệt vời! 💪',
  'Hướng nghiệp thú vị lắm, thử đi bạn ơi~',
  'Mỗi người đều có tài năng riêng, kể cả bạn! 🐱',
];

const MASCOT_IMAGES = {
  wave: '/mascot/cat-wave.png',
  study: '/mascot/cat-wave.png',
  celebrate: '/mascot/cat-wave.png',
};

export function CatMascot({
  message,
  variant = 'wave',
  autoHideMs = 8000,
  position = 'bottom-left',
  showOnMount = true,
}: CatMascotProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(
    message || DEFAULT_MESSAGES[Math.floor(Math.random() * DEFAULT_MESSAGES.length)]
  );

  // Check if user has dismissed mascot before
  useEffect(() => {
    const dismissed = sessionStorage.getItem('cat-mascot-dismissed');
    if (!dismissed && showOnMount) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [showOnMount]);

  // Auto hide speech bubble after delay
  useEffect(() => {
    if (isVisible && showBubble && autoHideMs > 0 && !isChatOpen) {
      const timer = setTimeout(() => setShowBubble(false), autoHideMs);
      return () => clearTimeout(timer);
    }
  }, [isVisible, showBubble, autoHideMs, isChatOpen]);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('cat-mascot-dismissed', 'true');
  };

  const handleCatClick = () => {
    if (isChatOpen) return;
    setIsChatOpen(true);
    setShowBubble(false);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    // Show a new message after closing chat
    setCurrentMessage(DEFAULT_MESSAGES[Math.floor(Math.random() * DEFAULT_MESSAGES.length)]);
    setShowBubble(true);
  };

  const handleMinimizeChat = () => {
    setIsChatOpen(false);
    setShowBubble(true);
    setCurrentMessage('Click vào mình để tiếp tục chat nhé! 💬');
  };

  if (!isVisible) return null;

  const positionClasses = {
    'bottom-right': 'right-4 sm:right-6',
    'bottom-left': 'left-4 sm:left-6',
  };

  return (
    <>
      {/* Chat Window */}
      <CatChat isOpen={isChatOpen} onClose={handleCloseChat} onMinimize={handleMinimizeChat} />

      {/* Mascot (hidden when chat is open) */}
      {!isChatOpen && (
        <div
          className={`fixed bottom-20 sm:bottom-6 ${positionClasses[position]} z-50 flex flex-col ${position === 'bottom-left' ? 'items-start' : 'items-end'} gap-2 animate-bounce-in`}
        >
          {/* Speech Bubble */}
          {showBubble && (
            <div className='relative bg-white dark:bg-gray-800 rounded-2xl px-3 py-2 shadow-lg border border-gray-200 dark:border-gray-700 max-w-[200px] sm:max-w-[220px] animate-fade-in'>
              <p className='text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed'>
                {currentMessage}
              </p>
              {/* Chat hint */}
              <div className='flex items-center gap-1 mt-1 text-[10px] text-amber-600 dark:text-amber-400'>
                <MessageCircle className='w-3 h-3' />
                <span>Click để chat</span>
              </div>
              {/* Speech bubble tail */}
              <div
                className={`absolute -bottom-2 ${position === 'bottom-left' ? 'left-8' : 'right-8'} w-4 h-4 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700 transform rotate-45`}
              />
            </div>
          )}

          {/* Cat Mascot */}
          <div className='relative group'>
            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className='absolute -top-2 -right-2 w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 z-10'
              aria-label='Ẩn mascot'
            >
              <X className='w-3 h-3 text-gray-600 dark:text-gray-400' />
            </button>

            {/* Chat indicator */}
            <div className='absolute -top-1 -left-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center animate-pulse z-10'>
              <MessageCircle className='w-3 h-3 text-white' />
            </div>

            {/* Cat Image */}
            <button
              onClick={handleCatClick}
              className='w-14 h-14 sm:w-16 sm:h-16 transition-all duration-300 hover:scale-110 cursor-pointer animate-wiggle drop-shadow-xl'
              aria-label='Chat với Mèo Học Bá'
            >
              <img
                src={MASCOT_IMAGES[variant]}
                alt='CAT Mascot - Linh vật mèo'
                className='w-full h-full object-contain'
              />
            </button>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: translateY(100px) scale(0.3);
          }
          50% {
            transform: translateY(-10px) scale(1.05);
          }
          70% {
            transform: translateY(5px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes wiggle {
          0%, 100% {
            transform: rotate(-3deg);
          }
          50% {
            transform: rotate(3deg);
          }
        }
        
        .animate-bounce-in {
          animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-wiggle {
          animation: wiggle 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}

export default CatMascot;
